'use client'

import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { cn } from './utils'

type ThemeName = 'default' | 'warm-onyx'

const themeOptions: { value: ThemeName; label: string; icon: typeof Sun }[] = [
  { value: 'default', label: 'Default', icon: Sun },
  { value: 'warm-onyx', label: 'Warm Onyx', icon: Moon },
]

function applyTheme(theme: ThemeName) {
  if (theme === 'warm-onyx') {
    document.documentElement.setAttribute('data-theme', 'warm-onyx')
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
  localStorage.setItem('theme', theme)
}

type ThemeSwitcherProps = {
  compact?: boolean
  className?: string
}

export function ThemeSwitcher({ compact = false, className }: ThemeSwitcherProps) {
  const [current, setCurrent] = useState<ThemeName>(() => {
    if (typeof window === 'undefined') return 'warm-onyx'
    return (localStorage.getItem('theme') as ThemeName | null) || 'warm-onyx'
  })

  useEffect(() => {
    const stored = (localStorage.getItem('theme') as ThemeName | null) || 'warm-onyx'
    setCurrent(stored)
    applyTheme(stored)
  }, [])

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex items-center gap-1 rounded-full border border-border/70 bg-muted/40 p-1">
        {themeOptions.map((option) => {
          const isActive = current === option.value
          const Icon = option.icon
          return (
            <button
              key={option.value}
              type="button"
              aria-label={option.label}
              title={option.label}
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold transition',
                isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => {
                setCurrent(option.value)
                applyTheme(option.value)
              }}
            >
              <Icon className="h-4 w-4" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
